import React, { useEffect, useRef, useContext, ReactElement } from 'react';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useTreeContext, useDragNode, useDropNode, useDragControl, useDragHandle } from './hooks';
import { PlaceholderContext } from './providers';
import { NodeModel, RenderParams } from './types';
import { Container } from './Container';
import { useTheme } from '@mui/material/styles';

export const Node = <T,>(props: any): ReactElement | null => {
    const theme: any = useTheme();
    const treeContext = useTreeContext();
    const placeholderContext = useContext(PlaceholderContext);
    const containerRef = useRef<HTMLLIElement>(null);
    const handleRef = useRef<any>(null);
    const item = props as NodeModel<T>;
    const { expandedItems, controlledExpandedNodes, classes } = treeContext;

    const isOpen = (): boolean =>
        controlledExpandedNodes?.includes?.(props.id) ?? expandedItems?.includes?.(props.id) ?? false;

    const [isMouseOver, setIsMouseOver] = React.useState(false);

    const [isDragging, drag, preview] = useDragNode(item, containerRef);
    const [isOver, , drop] = useDropNode<T>(item, props?.parentNode, containerRef, props?.disabled || isDragging);

    useDragHandle(containerRef, handleRef, drag);

    drop(containerRef);

    useEffect(() => {
        if (treeContext.dragPreviewRender) {
            preview(getEmptyImage(), { captureDraggingState: true });
        } else if (handleRef.current) {
            preview(containerRef);
        }
    }, [preview, treeContext.dragPreviewRender]);

    useDragControl(containerRef);

    const getPredefinedClassNamesObject = (): any => {
        let className = classes?.listItem ?? {};

        if (isOver && classes?.dropTarget) {
            className = { ...className, ...classes.dropTarget };
        }

        if (isDragging && classes?.draggingSource) {
            className = { ...className, ...classes.draggingSource };
        }

        return className;
    };

    const getClassName = (): string => {
        let className = 'dnd-item ';
        if (treeContext?.dnd && isMouseOver) {
            className += 'dnd-hierarchy bg-content-wrapper';
        } else if (treeContext?.dnd && !isMouseOver) {
            className += 'dnd-hierarchy bg-white';
        }
        return className;
    };

    const draggable = treeContext.canDrag ? treeContext.canDrag(props.id) : true;
    const isDropTarget = placeholderContext.dropTargetId === props.id;

    const params: RenderParams = {
        depth: props.depth,
        isOpen: isOpen(),
        isDragging,
        isDropTarget,
        draggable,
        containerRef,
        handleRef,
        controlledSelected: props?.controlledSelected,
        filterPayload: props?.filterPayload,
    };
    const [hierarchyMappingDataId, setHierarchyMappingDataId] = React.useState(props?.id ?? '');

    useEffect(() => {
        if (typeof treeContext?.hierarchyMappingData?.[props?.id] === 'string') {
            setHierarchyMappingDataId(treeContext?.hierarchyMappingData?.[props?.id] ?? '');
        } else {
            setHierarchyMappingDataId(props?.id ?? '');
        }
    }, [treeContext?.hierarchyMappingData]);

    const getControlledSelected = (): boolean | undefined => {
        if (
            (typeof treeContext?.autoChildSelection === 'boolean' && treeContext?.autoChildSelection) ||
            (typeof treeContext?.autoChildSelection === 'function' && treeContext?.autoChildSelection?.(props))
        ) {
            return (props?.controlledSelected || treeContext?.selectedNodes?.includes(props?.id)) ?? false;
        }
        return false;
    };

    const isNodeExpanded =
        controlledExpandedNodes?.includes?.(props?.id) ?? expandedItems?.includes?.(props?.id) ?? false;
    const shouldRenderChildren =
        !treeContext?.loadingNode?.includes?.(props?.id) &&
        Boolean(treeContext?.hierarchyMappingData?.[hierarchyMappingDataId]) &&
        isNodeExpanded;

    return (
        <li
            className={getClassName()}
            ref={!props?.disabled ? containerRef : undefined}
            data-testid={`hierarchy-node-${props?.id}`}
            style={{
                ...getPredefinedClassNamesObject(),
                ...(isDropTarget &&
                !isDragging &&
                !treeContext?.hierarchyMappingData?.[hierarchyMappingDataId]?.length &&
                !props?.disabled
                    ? {
                          outline: `3px solid ${theme?.palette?.primary?.main}`,
                          backgroundColor: '#007BC10D',
                          color: theme?.palette?.primary?.main,
                      }
                    : {}),
                ...(treeContext?.dnd ? { margin: '3px' } : {}),
            }}
        >
            {treeContext.render(item, props?.parentNode, params, { isMouseOver, setIsMouseOver })}
            {shouldRenderChildren && (
                <Container
                    parentNode={props}
                    depth={props.depth + 1}
                    nodes={treeContext?.hierarchyMappingData?.[hierarchyMappingDataId]}
                    controlledSelected={getControlledSelected()}
                    disabled={isDragging || props?.disabled}
                />
            )}
        </li>
    );
};
